import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import messages from "../../../../../assets/locale/messages";
import { eventTypes } from "../../../../../utils/Constants";
import { getIndexPermissions, textContainOnlySpaces } from "../../../../../utils/Helpers";
import {
  isSuperAdmin,
  isVisitor,
} from "../../../../../utils/Permissions";
import {
  buildAddSlotPayload,
  buildEditSlotPayload,
} from "../../../../../components/Events/Event/timesheet/modals";
import {
  TIMESHEET_SLOT_TABS,
} from "../../../../../components/Events/Event/timesheet/constants";
import {
  mapTrainingWorkingSlotRow,
  mapWorkingLogToApplicantRow,
  mapWorkingSlotRow,
} from "../../../../../components/Events/Event/timesheet/timesheetApiMappers";
import {
  buildTimesheetNameOrderParams,
  buildTimesheetWorkingLogsSearchParams,
  buildTimesheetStatusQueryParams,
  filterTimesheetRowsByStatus,
  needsTimesheetClientStatusFilter,
  paginateRows,
  shouldSkipTimesheetSearchQuery,
} from "../../../../../components/Events/Event/timesheet/timesheetListHelpers";
import { getTimesheetApplicantName } from "../../../../../components/Events/Event/timesheet/timesheetUtils";
import { getTimesheetStatusOptions } from "../../../../../components/Events/Event/timesheet/timesheetStatusOptions";
import { normalizeTimesheetApplicantId } from "../../../../../components/Events/Event/timesheet/timesheetUtils";
import {
  addApplicantTimeSheet,
  deleteApplicantTimeSheetRequest,
  editApplicantTimeSheetRequest,
  getApplicantstTimeSheetRequests,
} from "../../../../../store/ApplicantsTimeSheet/actions";
import {
  addTrainingWorkingSlot,
  deleteTrainingWorkingSlotRequest,
  editTrainingWorkingSlotRequest,
  getTrainingWorkingSlotsRequest,
} from "../../../../../store/TrainingWorkingSlots/actions";
import { getDailyAndMonthlyReportsRequests } from "../../../../../store/TimeSheet/actions";

const buildPaginationMeta = (meta = {}, page, pageSize) => {
  const currentPage = meta.currentPage || page;
  const total = meta.count || 0;
  const pages = meta.pages || 1;

  return {
    page: currentPage,
    total,
    pages,
    rangeFrom: total ? (currentPage - 1) * pageSize + 1 : 0,
    rangeTo: Math.min(currentPage * pageSize, total),
  };
};

const useAdminEventTimesheet = (event) => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const permissions = useSelector((state) => state.auth.permission);
  const workingLogs = useSelector((state) => state.timesheet.list);
  const workingLogsMeta = useSelector((state) => state.timesheet.meta);
  const shiftsList = useSelector((state) => state.applicantsTimeSheetRequest.list);
  const shiftsMeta = useSelector((state) => state.applicantsTimeSheetRequest.meta);
  const trainingsList = useSelector((state) => state.trainingWorkingSlots.list);

  const { events } = messages[lang];
  const copy = events.viewEvents.timesheetTab;
  const timesheetPermission = getIndexPermissions(permissions, "Timesheet");
  const canAddSlot =
    isSuperAdmin() || (isVisitor() && Boolean(timesheetPermission?.can_add));
  const canEditSlot =
    isSuperAdmin() || (isVisitor() && Boolean(timesheetPermission?.can_edit));
  const canDeleteSlot =
    isSuperAdmin() || (isVisitor() && Boolean(timesheetPermission?.can_delete));

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({
    role: null,
    status: null,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [shiftsPage, setShiftsPage] = useState(1);
  const [shiftsPageSize, setShiftsPageSize] = useState(10);
  const [selectedApplicantId, setSelectedApplicantId] = useState(null);
  const [resolvedApplicant, setResolvedApplicant] = useState(null);

  const eventId = event?.id;
  const needsClientStatusFilter = needsTimesheetClientStatusFilter(filters.status);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const buildWorkingLogsParams = useCallback(
    (searchValue = debouncedSearch) => {
      const query = searchValue.trim();
      const params = {
        event_id: eventId,
        ...buildTimesheetWorkingLogsSearchParams(query),
        ...buildTimesheetNameOrderParams(lang),
        role_id: filters.role?.id ? String(filters.role.id) : undefined,
        ...buildTimesheetStatusQueryParams(filters.status),
      };

      if (needsClientStatusFilter) {
        return { ...params, page: -1 };
      }

      return {
        ...params,
        page,
        items: pageSize,
      };
    },
    [
      debouncedSearch,
      eventId,
      filters.role?.id,
      filters.status,
      lang,
      needsClientStatusFilter,
      page,
      pageSize,
    ],
  );

  const refetchWorkingLogs = useCallback(() => {
    if (!eventId) {
      return;
    }

    dispatch(getDailyAndMonthlyReportsRequests(buildWorkingLogsParams()));
  }, [buildWorkingLogsParams, dispatch, eventId]);

  useEffect(() => {
    if (!eventId) {
      return;
    }

    const query = debouncedSearch.trim();
    if (shouldSkipTimesheetSearchQuery(query)) {
      return;
    }

    if (textContainOnlySpaces(query)) {
      return;
    }

    refetchWorkingLogs();
  }, [debouncedSearch, eventId, refetchWorkingLogs]);

  const applicants = useMemo(() => {
    const rows = (workingLogs || []).map(mapWorkingLogToApplicantRow);
    const filteredRows = needsClientStatusFilter
      ? filterTimesheetRowsByStatus(rows, filters.status)
      : rows;
    const locale = lang === "ar" ? "ar" : "en";

    return [...filteredRows].sort((left, right) =>
      getTimesheetApplicantName(left, lang).localeCompare(
        getTimesheetApplicantName(right, lang),
        locale,
        { sensitivity: "base" },
      ),
    );
  }, [filters.status, lang, needsClientStatusFilter, workingLogs]);

  const selectedApplicant = useMemo(() => {
    const normalizedId = normalizeTimesheetApplicantId(selectedApplicantId);
    if (!normalizedId) {
      return null;
    }

    const fromList =
      applicants.find(
        (row) => normalizeTimesheetApplicantId(row.id) === normalizedId,
      ) ?? null;

    if (fromList) {
      return fromList;
    }

    return resolvedApplicant;
  }, [applicants, resolvedApplicant, selectedApplicantId]);

  useEffect(() => {
    const normalizedId = normalizeTimesheetApplicantId(selectedApplicantId);
    if (!normalizedId) {
      setResolvedApplicant(null);
      return;
    }

    const fromList = applicants.find(
      (row) => normalizeTimesheetApplicantId(row.id) === normalizedId,
    );
    if (fromList) {
      setResolvedApplicant(fromList);
    }
  }, [applicants, selectedApplicantId]);

  useEffect(() => {
    const normalizedId = normalizeTimesheetApplicantId(selectedApplicantId);
    if (!normalizedId || !eventId) {
      return;
    }

    if (
      applicants.some(
        (row) => normalizeTimesheetApplicantId(row.id) === normalizedId,
      )
    ) {
      return;
    }

    const firstSlot = shiftsList?.[0];
    if (!firstSlot?.user) {
      return;
    }

    setResolvedApplicant((current) => {
      if (normalizeTimesheetApplicantId(current?.id) === normalizedId) {
        return current;
      }

      return {
        id: normalizedId,
        working_log_id: Number(normalizedId) || normalizedId,
        user: firstSlot.user,
        working_days: 0,
        training_days: 0,
        status: null,
      };
    });
  }, [applicants, eventId, selectedApplicantId, shiftsList]);

  const buildShiftsParams = useCallback(
    (applicant = selectedApplicant) => {
      const workingLogId =
        applicant?.working_log_id ?? applicant?.id ?? selectedApplicantId;

      if (!eventId || !workingLogId) {
        return null;
      }

      const params = {
        page: shiftsPage,
        items: shiftsPageSize,
        event_id: eventId,
        working_log_id: workingLogId,
        order_by_check_in_at: "desc",
      };

      if (applicant?.user?.id) {
        params.user_id = applicant.user.id;
      }

      return params;
    },
    [eventId, selectedApplicant, selectedApplicantId, shiftsPage, shiftsPageSize],
  );

  const buildTrainingsParams = useCallback(
    (applicant = selectedApplicant) => {
      const workingLogId =
        applicant?.working_log_id ?? applicant?.id ?? selectedApplicantId;

      if (!eventId || !workingLogId) {
        return null;
      }

      const params = {
        page: -1,
        event_id: eventId,
        working_log_id: workingLogId,
        locale: lang,
      };

      if (applicant?.user?.id) {
        params.user_id = applicant.user.id;
      }

      return params;
    },
    [eventId, lang, selectedApplicant, selectedApplicantId],
  );

  useEffect(() => {
    if (!selectedApplicantId) {
      return;
    }

    const shiftsParams = buildShiftsParams();
    const trainingsParams = buildTrainingsParams();

    if (!shiftsParams || !trainingsParams) {
      return;
    }

    dispatch(getApplicantstTimeSheetRequests(shiftsParams));
    dispatch(getTrainingWorkingSlotsRequest(trainingsParams));
  }, [
    buildShiftsParams,
    buildTrainingsParams,
    dispatch,
    selectedApplicantId,
  ]);

  const listPagination = useMemo(() => {
    if (needsClientStatusFilter) {
      return {
        ...paginateRows(applicants, page, pageSize),
        pageSize,
      };
    }

    const meta = buildPaginationMeta(workingLogsMeta, page, pageSize);

    return {
      rows: applicants,
      pageSize,
      ...meta,
    };
  }, [
    applicants,
    needsClientStatusFilter,
    page,
    pageSize,
    workingLogsMeta,
  ]);

  const shiftsRows = useMemo(
    () => (shiftsList || []).map(mapWorkingSlotRow),
    [shiftsList],
  );

  const shiftsPagination = useMemo(() => {
    const meta = buildPaginationMeta(shiftsMeta, shiftsPage, shiftsPageSize);

    return {
      rows: shiftsRows,
      pageSize: shiftsPageSize,
      ...meta,
    };
  }, [shiftsMeta, shiftsPage, shiftsPageSize, shiftsRows]);

  const trainingsRows = useMemo(
    () => (trainingsList || []).map(mapTrainingWorkingSlotRow),
    [trainingsList],
  );

  const statusOptions = useMemo(
    () => getTimesheetStatusOptions(copy.statuses),
    [copy.statuses],
  );

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const handleShiftsPageSizeChange = (nextSize) => {
    setShiftsPageSize(nextSize);
    setShiftsPage(1);
  };

  const handleSlotSave = (formValues, { mode, slot, slotType, onSuccess, onError }) => {
    const userId = selectedApplicant?.user?.id;
    if (!eventId || !userId) {
      return false;
    }
    const isEdit = mode === "edit";
    const payload = isEdit
      ? buildEditSlotPayload(slot?.id, formValues)
      : buildAddSlotPayload(formValues);
    const refetchParams =
      slotType === TIMESHEET_SLOT_TABS.shifts
        ? buildShiftsParams()
        : buildTrainingsParams();
    const refetchWorkingLogsParams = buildWorkingLogsParams();
    const callbacks = { onSuccess, onError };

    if (slotType === TIMESHEET_SLOT_TABS.shifts) {
      if (isEdit) {
        dispatch(
          editApplicantTimeSheetRequest({
            id: payload.id,
            data: {
              check_in_at: payload.check_in_at,
              check_out_at: payload.check_out_at,
            },
            event_id: eventId,
            user_id: userId,
            refetchParams,
            refetchWorkingLogs: refetchWorkingLogsParams,
            ...callbacks,
          }),
        );
      } else {
        dispatch(
          addApplicantTimeSheet({
            data: {
              event_id: eventId,
              user_id: userId,
              check_in_at: payload.check_in_at,
              check_out_at: payload.check_out_at,
            },
            refetchParams,
            refetchWorkingLogs: refetchWorkingLogsParams,
            ...callbacks,
          }),
        );
      }
      return true;
    }

    if (isEdit) {
      dispatch(
        editTrainingWorkingSlotRequest({
          id: payload.id,
          data: {
            check_in_at: payload.check_in_at,
            check_out_at: payload.check_out_at,
          },
          event_id: eventId,
          user_id: userId,
          refetchParams,
          refetchWorkingLogs: refetchWorkingLogsParams,
          ...callbacks,
        }),
      );
      return true;
    }

    dispatch(
      addTrainingWorkingSlot({
        data: {
          event_id: eventId,
          user_id: userId,
          check_in_at: payload.check_in_at,
          check_out_at: payload.check_out_at,
        },
        refetchParams,
        refetchWorkingLogs: refetchWorkingLogsParams,
        ...callbacks,
      }),
    );
    return true;
  };

  const handleSlotDelete = (slot, slotType) => {
    if (!eventId || !selectedApplicant?.user?.id || !slot?.id) {
      return;
    }

    const userId = selectedApplicant.user.id;
    const refetchParams =
      slotType === TIMESHEET_SLOT_TABS.shifts
        ? buildShiftsParams()
        : buildTrainingsParams();
    const refetchWorkingLogsParams = buildWorkingLogsParams();

    if (slotType === TIMESHEET_SLOT_TABS.shifts) {
      dispatch(
        deleteApplicantTimeSheetRequest({
          id: slot.id,
          eventId,
          userId,
          refetchParams,
          refetchWorkingLogs: refetchWorkingLogsParams,
        }),
      );
      return;
    }

    dispatch(
      deleteTrainingWorkingSlotRequest({
        id: slot.id,
        eventId,
        userId,
        refetchParams,
        refetchWorkingLogs: refetchWorkingLogsParams,
      }),
    );
  };

  return {
    eventType: eventTypes.daily,
    search,
    filters,
    statusOptions,
    applicants,
    selectedApplicant,
    setSelectedApplicantId: (id) =>
      setSelectedApplicantId(normalizeTimesheetApplicantId(id)),
    listPagination,
    shiftsPagination,
    trainingsRows,
    onSearchChange: handleSearchChange,
    onFilterChange: handleFilterChange,
    onPageChange: setPage,
    onPageSizeChange: handlePageSizeChange,
    onShiftsPageChange: setShiftsPage,
    onShiftsPageSizeChange: handleShiftsPageSizeChange,
    onSlotSave: handleSlotSave,
    onSlotDelete: handleSlotDelete,
    refetchWorkingLogs,
    canAddSlot,
    canEditSlot,
    canDeleteSlot,
  };
};

export default useAdminEventTimesheet;
